import { useState, useEffect, useMemo } from 'react'
import type { Theme } from '@mui/material/styles'
import { lightTheme } from '../theme/lightTheme'
import { darkTheme } from '../theme/darkTheme'
import { browserStorage, type StorageAdapter } from '../lib/storage'
import { getBrowserAdapter } from '../lib/browserAdapter'

export type ColorMode = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'colorMode'

/**
 * Detects the system color scheme preference.
 * Returns 'light' or 'dark' based on prefers-color-scheme media query.
 * Falls back to 'light' if the preference cannot be read.
 *
 * @returns 'light' | 'dark'
 */
function getSystemPreference(): 'light' | 'dark' {
    try {
        const browserAdapter = getBrowserAdapter()
        const mediaQuery = browserAdapter.matchMedia('(prefers-color-scheme: dark)')
        return mediaQuery.matches ? 'dark' : 'light'
    } catch {
        return 'light'
    }
}

/**
 * Hook for managing color mode (system/light/dark theme) preference.
 * Stores preference in storage adapter and provides theme switching functionality.
 * System mode follows the browser/OS preference and reacts to runtime changes.
 *
 * @param storage - Optional storage adapter (defaults to browserStorage for production)
 * @returns Object with current colorMode, effective theme, and setColorMode function
 */
export function useColorMode(storage: StorageAdapter = browserStorage) {
    const [colorMode, setColorModeState] = useState<ColorMode>(() => {
        // Initialize from storage or default to 'system'
        const stored = storage.getItem(STORAGE_KEY)
        if (stored === 'system' || stored === 'light' || stored === 'dark') {
            return stored
        }
        return 'system'
    })

    // Track system preference for reactive updates in System mode
    const [systemPreference, setSystemPreference] = useState<'light' | 'dark'>(() =>
        getSystemPreference()
    )

    // Listen to system preference changes (only relevant when in System mode)
    useEffect(() => {
        try {
            const browserAdapter = getBrowserAdapter()
            const mediaQuery = browserAdapter.matchMedia('(prefers-color-scheme: dark)')
            const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
                try {
                    const newPreference = e.matches ? 'dark' : 'light'
                    setSystemPreference(newPreference)
                } catch {
                    // Fallback to light if reading preference fails
                    setSystemPreference('light')
                }
            }

            // Set initial value
            try {
                handleChange(mediaQuery)
            } catch {
                // Fallback to light if initial read fails
                setSystemPreference('light')
            }

            // Listen for changes (modern browsers)
            if (mediaQuery.addEventListener) {
                mediaQuery.addEventListener('change', handleChange)
                return () => {
                    mediaQuery.removeEventListener('change', handleChange)
                }
            } else {
                // Fallback for older browsers
                mediaQuery.addListener(handleChange)
                return () => {
                    mediaQuery.removeListener(handleChange)
                }
            }
        } catch {
            // Fallback to light if matchMedia is not available
            setSystemPreference('light')
            return undefined
        }
    }, [])

    // Update storage when colorMode changes
    useEffect(() => {
        try {
            storage.setItem(STORAGE_KEY, colorMode)
        } catch {
            // Gracefully handle storage errors (e.g., localStorage unavailable)
            // Continue without crashing
        }
    }, [colorMode, storage])

    // Get the effective theme mode (system → light/dark based on system preference)
    const effectiveMode = useMemo<'light' | 'dark'>(() => {
        if (colorMode === 'system') {
            return systemPreference
        }
        return colorMode
    }, [colorMode, systemPreference])

    // Get the current theme based on effective mode
    const theme = useMemo<Theme>(() => {
        return effectiveMode === 'dark' ? darkTheme : lightTheme
    }, [effectiveMode])

    // Set color mode (updates state and localStorage)
    const setColorMode = (mode: ColorMode) => {
        setColorModeState(mode)
    }

    return {
        colorMode,
        theme,
        setColorMode,
    }
}
